import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import BadgeCabinet from '@/components/BadgeCabinet';
import { localizeBadge } from '@/lib/badges';
import { useLang } from '@/context/LangContext';
import { Trophy, Award } from 'lucide-react';

const CAT_KEYS = {
  creation: 'cat_creation',
  roster: 'cat_roster_t',
  sales: 'cat_sales',
  buying: 'cat_buying',
  quality: 'cat_quality',
  status: 'cat_status',
  ai: 'cat_ai',
};

export default function Achievements() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/achievements/me').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout><div className="text-gray-400">{t('loading')}</div></Layout>;
  if (!data) return <Layout><div className="text-gray-400">{t('achievements_no_data')}</div></Layout>;

  const progress = Math.round((data.unlocked.length / data.total_available) * 100);

  // Localize badges & group by category
  const catalog = (data.catalog || []).map(b => localizeBadge(b, lang));
  const unlocked = (data.unlocked || []).map(u => ({ ...u, badge: localizeBadge(u.badge, lang) }));

  const groups = {};
  catalog.forEach(b => { (groups[b.category] = groups[b.category] || []).push(b); });

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('achievements_eyebrow')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <Trophy className="w-9 h-9 text-gold" /> {t('achievements_h1')}
        </h1>
        <p className="text-gray-400 mt-2">
          {t('achievements_subtitle_a')} {unlocked.length}/{data.total_available} {t('achievements_subtitle_b')}
        </p>
      </div>

      <div className="bg-card-steel border border-gold/30 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-widest text-gold">{t('achievements_progress')}</div>
          <div className="font-h text-2xl text-gold">{progress}%</div>
        </div>
        <div className="h-3 bg-navy-deep rounded-full overflow-hidden border border-steel">
          <div className="h-full bg-gradient-to-r from-amber-600 via-gold to-fuchsia-400 transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {unlocked.length > 0 && (
        <div className="bg-card-steel border-2 border-gold rounded-lg p-5 mb-6">
          <div className="text-xs font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-1">
            <Award className="w-3 h-3" /> {t('achievements_latest')}
          </div>
          <BadgeCabinet
            unlocked={unlocked.slice(0, 6)}
            catalog={unlocked.slice(0, 6).map(u => u.badge)}
            showLocked={false}
            size="md"
          />
        </div>
      )}

      {Object.entries(groups).map(([cat, badges]) => (
        <div key={cat} className="bg-card-steel border border-gold/30 rounded-lg p-5 mb-4">
          <h2 className="font-h uppercase text-lg text-gold mb-4">{t(CAT_KEYS[cat]) || cat}</h2>
          <BadgeCabinet unlocked={unlocked} catalog={badges} size="md" />
        </div>
      ))}
    </Layout>
  );
}
